import { store } from "../app/store";
import {
  clearBooking,
  clearBookingPayment,
} from "../features/booking/bookingSlice";
import { type IHotelCurrency, type IHotelState } from "../features/hotel/hotelInterface";
import {
  getConfiguration,
  getHotelConfiguration,
  getHotelPaymentConfiguration,
  setCulture,
  setId,
} from "../features/hotel/hotelSlice";
import { hotels } from "../config/hotels";
import { syncCultureWithI18n, syncI18nWithCulture } from "./language";

/**
 * Find a hotel in the hotel config
 * @param HotelCode
 * @returns
 */
export const findHotel = (HotelCode: IHotelState["HotelCode"]) => {
  return hotels.find((h) => h.HotelCode === HotelCode);
};

/**
 * Get the current hotel name from the config file
 *
 * @returns hotel name
 */
export const currentHotelName = (): string => {
  const { hotel } = store.getState();
  const hotelConfig = findHotel(hotel.HotelCode);
  return hotelConfig?.Name || "";
};

export const setHotelConfiguration = async ({
  HotelCode,
}: {
  HotelCode: IHotelState["HotelCode"];
}) => {

  
  /**
   * Remember current culture
  */
  const { hotel } = store.getState();

  
  const culture = {
    LanguageCode: hotel.Culture.LanguageCode || null,
    CultureCode: hotel.Culture.LanguageCode.split("-")[1] || null,
    CurrencyCode: hotel.Culture.CurrencyCode || null,
  };

  // Find the hotel in the config
  const newHotel = findHotel(HotelCode);

  // If the hotel is found, set the configuration
  if (newHotel !== undefined) {
    const HotelCode: IHotelState["HotelCode"] = newHotel.HotelCode;
    const Id: IHotelState["Id"] = newHotel.Id;
    const ConfigurationId: IHotelState["ConfigurationId"] =
      newHotel.ConfigurationId;
    const DirectPaymentId: IHotelState["DirectPaymentId"] =
      newHotel.DirectPaymentId;

    let DPRateGroupID: string = "";
    if (DirectPaymentId.length > 15) {
      DPRateGroupID = DirectPaymentId;
    }

    // Add new hotel
    store.dispatch(
      setId({ Id, HotelCode, ConfigurationId, DirectPaymentId: DPRateGroupID })
    );
    store.dispatch(getConfiguration(ConfigurationId));


    store.dispatch(getHotelPaymentConfiguration(Id));

    /**
     * Clear booking state
     */
    store.dispatch(clearBooking());
    store.dispatch(clearBookingPayment());

    /**
     *  Get hotel config
     *  Clear booking state
     */
    await store.dispatch(getHotelConfiguration(Id)).then((hotelConfig) => {
      /**
       * Set the new hotel culture
       */
      let currencyCode = culture.CurrencyCode
      if(!hotelConfig.payload?.Currencies.some((c: IHotelCurrency) => {
        return c.Code === culture.CurrencyCode;
      })){
        // Currency is not available in new hotel
        currencyCode = hotelConfig.payload?.DefaultCurrencyCode;
      }
      
      const languageCode =
        culture.LanguageCode ||
        hotelConfig.payload?.DefaultLanguageCode ||
        "en-US";

      store.dispatch(
        setCulture({
          LanguageCode: languageCode,
          CultureCode:
            culture.CultureCode ||
            languageCode.split("-")[1] ||
            "US",
          CurrencyCode: currencyCode || "EUR",
        })
      );

      syncCultureWithI18n(HotelCode);
      syncI18nWithCulture(
        store.getState().hotel.Culture.LanguageCode
      );

      return true;
    });
  }
};

/**
 * Format address from {
      Line1: 'Bellamystraat 3',
      Line2: null,
      City: 'Amsterdam',
      PostalCode: '1053 RT',
      CountryCode: 'NL',
      Latitude: 52.367238,
      Longitude: 4.8670224
    } to "Bellamystraat 3, Amsterdam, 1053 RT, NL"
 * @param address
 * @returns
 */  
export const formatHotelAddress = (address: { [key: string]: string }): string => {
  const parts: string[] = [];
  if (address.Line1) parts.push(address.Line1);
  if (address.Line2) parts.push(address.Line2);
  if (address.PostalCode) parts.push(address.PostalCode);
  if (address.City) parts.push(address.City);
  return parts.join(", ");
}
